// PeakHoursChart.jsx - FULLY CORRECTED VERSION
import React, { useState, useMemo } from "react";

const PeakHoursChart = ({ data = [] }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, data: null });
  
  // Process real API data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { hours: [], values: [], maxValue: 0, totalAttendees: 0 };
    
    // Sort by hour and extract values
    const sorted = [...data].sort((a, b) => {
      const hourA = parseInt(a.hour?.split(':')[0] || 0);
      const hourB = parseInt(b.hour?.split(':')[0] || 0);
      return hourA - hourB;
    });
    
    const hours = sorted.map(d => d.hour);
    const values = sorted.map(d => d.count || d.attendees || 0);
    const maxValue = Math.max(...values, 1) * 1.1;
    const totalAttendees = values.reduce((sum, v) => sum + v, 0);
    
    return { hours, values, maxValue, totalAttendees };
  }, [data]);

  // Calculate bar positions
  const barWidth = 45;
  const barSpacing = 10;
  const startX = 80;
  
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Find which bar is under cursor
    const barIndex = Math.floor((x - startX) / (barWidth + barSpacing));
    
    if (barIndex >= 0 && barIndex < chartData.hours.length) {
      setTooltip({
        visible: true,
        x: x + 20,
        y: y - 40,
        data: {
          hour: chartData.hours[barIndex],
          value: chartData.values[barIndex]
        }
      });
    } else {
      setTooltip({ visible: false, x: 0, y: 0, data: null });
    }
  };
  
  const handleMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, data: null });
  };

  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <div className="peak-hours-container">
        <h2 className="peak-hours-title">Peak Hours</h2>
        <div className="chart-empty-state">
          <p>No check-in time data available</p>
          <p style={{fontSize: '11px', color: '#999'}}>
            Backend doesn't provide check-in timestamps
          </p>
        </div>
      </div>
    );
  }

  const { hours, values, maxValue, totalAttendees } = chartData;
  
  return (
    <div className="peak-hours-container">
      <h2 className="peak-hours-title">Peak Hours {hours.length > 0 && `— ${hours[0]?.split(' ')[0]}`}</h2>
      
      <div 
        className="peak-hours-chart"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <svg viewBox="0 0 600 300" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
          {/* Grid Lines */}
          <g stroke="#e2e8f0" strokeWidth="1">
            <line x1="60" y1="240" x2="580" y2="240" />
            <line x1="60" y1="180" x2="580" y2="180" />
            <line x1="60" y1="120" x2="580" y2="120" />
            <line x1="60" y1="60" x2="580" y2="60" />
          </g>
          
          {/* Y-Axis Labels */}
          <g fontSize="12" fill="#64748b" textAnchor="end" fontFamily="Inter, sans-serif">
            <text x="50" y="245">0</text>
            <text x="50" y="185">{Math.round(maxValue * 0.25)}</text>
            <text x="50" y="125">{Math.round(maxValue * 0.5)}</text>
            <text x="50" y="65">{Math.round(maxValue * 0.75)}</text>
            <text x="50" y="5">{Math.round(maxValue)}</text>
          </g>
          
          {/* X-Axis Labels */}
          <g fontSize="12" fill="#64748b" textAnchor="middle" fontFamily="Inter, sans-serif">
            {hours.map((hour, i) => (
              <text key={i} x={startX + i * (barWidth + barSpacing) + barWidth/2} y="270">
                {hour}
              </text>
            ))}
          </g>
          
          {/* Bars - Using real data */}
          {values.map((value, i) => {
            const barHeight = (value / maxValue) * 200;
            return (
              <rect
                key={i}
                x={startX + i * (barWidth + barSpacing)}
                y={240 - barHeight}
                width={barWidth}
                height={barHeight}
                fill="#f97316"
                rx="4"
              />
            );
          })}
          
          {/* Tooltip trigger area */}
          <rect x="0" y="0" width="600" height="300" fill="transparent" />
        </svg>
        
        {/* Tooltip */}
        {tooltip.visible && tooltip.data && (
          <div 
            className="chart-tooltip" 
            style={{ 
              left: tooltip.x, 
              top: tooltip.y 
            }}
          >
            <span><span className="label">{tooltip.data.hour}</span></span>
            <span><span className="label">Attendees:</span> <span className="value">{tooltip.data.value}</span></span>
          </div>
        )}
      </div>
      
      {/* Statistics - Calculated from real data */}
      <div className="chart-stats">
        <div className="stat-item">
          <span className="stat-label">Total Check-ins:</span>
          <span className="stat-value">{totalAttendees}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Peak Hour:</span>
          <span className="stat-value">
            {values.length > 0 ? hours[values.indexOf(Math.max(...values))] : 'N/A'}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Peak Count:</span>
          <span className="stat-value">{Math.max(...values, 0)}</span>
        </div>
      </div>
    </div>
  );
};

export default PeakHoursChart;